import Messages from '../../components/viewReport/messages';
import {
  ReportDetails,
  ReportDetailsProp,
} from '../../components/viewReport/reportDetails';

import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useIntl } from 'react-intl';
import useDecryption from '~/hooks/cryptography/decryptText';
import { axiosInstance } from '../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../assets/utils/helpers';
import { WebSocketMessage } from '../../components/viewReport/messages';
import ErrorType from '../../interfaces/ErrorType';
import SubmitFeedbackForm from './modal/submitFeedbackForm';
import SubmitFeedbackSuccess from './modal/submitFeedbackSuccess';

interface FileFromReportDetailsOriginalProps {
  id: number;
  original_file_name: string;
  file_type: string;
  url: string;
}
interface AdditionalInformationOriginalProps {
  id: number;
  description_text: string;
  files: Array<FileFromReportDetailsOriginalProps>;
}

const ViewReportPage = () => {
  const { reportDetailsId } = useParams();
  const { decryptText } = useDecryption();
  const intl = useIntl();
  const [reportDetails, setReportDetails] = useState<ReportDetailsProp>({
    id: '',
    formType: -1,
    reportType: 0,
    description: '',
    createdAt: '',
    updatedAt: '',
    files: [],
    additionalInformation: [],
    referenceKey: '',
    conversation: '',
    dueDate: '',
    finalDueDate: '',
    status: 0,
    voiceReport: null,
    reportTypeName: '',
    formattedCreatedAt: '',
    formattedUpdatedAt: '',
    userName: '',
    country: null,
    statusName: '',
    tenantName: '',
    customFieldValues: [],
    hasFeedbackBeenSubmitted: false,
    isWhistleblowerFeedbackEnabled: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [messageList, setList] = useState<WebSocketMessage[]>([]);
  const [enableDownload, setEnableDownload] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);

  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });

  const transformFiles = (files: Array<FileFromReportDetailsOriginalProps>) => {
    return files.map(
      ({
        id,
        original_file_name,
        file_type,
        url,
      }: FileFromReportDetailsOriginalProps) => {
        return {
          id,
          originalFileName: original_file_name,
          fileType: file_type,
          url,
        };
      },
    );
  };

  const handleClickFeedback = async () => {
    setShowFeedbackForm((previousState) => !previousState);
  };

  const handleClickSuccess = async () => {
    setShowFeedbackSuccess((previousState) => !previousState);
  };

  const handleEnableDownload = (value: boolean) => {
    setEnableDownload(value);
  };

  const fetchReportDetailsDropdownHandler = useCallback(async () => {
    setIsLoading(true);
    setError({
      errorType: false,
      message: '',
    });
    try {
      const API_PATH = import.meta.env.VITE_API_PATH ?? '';
      const LINK = '/reports';
      const IDENTIFIER = `/${reportDetailsId}`;
      const apiUrl = `${
        import.meta.env.VITE_HTTPS_PROTOCOL
      }${API_PATH}${LINK}${IDENTIFIER}`;

      const response = await axiosInstance().get(`${apiUrl}`);

      const data = response.data;

      // Description in-transit decryption before displaying
      const descriptionText = await decryptText(data.description.description);

      const {
        conversation,
        status,
        form_type: formType,
        id: reportId,
        report_type: reportType,
        created_at: createdAt,
        updated_at: updatedAt,
        files: reportDetailsFiles,
        additional_information: additionalInformation,
        reference_key: referenceKey,
        due_date: dueDate,
        final_due_date: finalDueDate,
        voice_report: voiceReport,
        report_type_name: reportTypeName,
        created_at_formatted_date: formattedCreatedAt,
        updated_at_formatted_date: formattedUpdatedAt,
        user_full_name: userName,
        country_verbose: country,
        status_name: statusName,
        tenant_name: tenantName,
        report_custom_field_values: reportCustomFieldValues,
        has_feedback_been_submitted: hasFeedbackBeenSubmitted,
        is_whistleblower_feedback_enabled: isWhistleblowerFeedbackEnabled,
      } = data;

      const objForView = {
        conversation,
        country,
        createdAt,
        formattedCreatedAt,
        formattedUpdatedAt,
        referenceKey,
        reportType,
        reportTypeName,
        status,
        statusName,
        tenantName,
        updatedAt,
        dueDate,
        finalDueDate,
        userName,
        voiceReport,
        id: reportId,
        formType,
        description: descriptionText,
        files: transformFiles(reportDetailsFiles),
        additionalInformation:
          additionalInformation && additionalInformation.length > 0
            ? additionalInformation.map(
                ({
                  id,
                  description_text,
                  files,
                }: AdditionalInformationOriginalProps) => {
                  return {
                    id,
                    descriptionText: description_text,
                    files:
                      files && files.length > 0 ? transformFiles(files) : [],
                  };
                },
              )
            : [],
        customFieldValues: reportCustomFieldValues,
        hasFeedbackBeenSubmitted,
        isWhistleblowerFeedbackEnabled,
      };
      setReportDetails(objForView);

      setIsLoading(false);
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: "Error happened. We're currently fixing it.",
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Report details not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError({
        errorType: true,
        message: errorMessage,
      });
      setIsLoading(false);
    }
  }, [reportDetailsId]);

  useEffect(() => {
    if (
      (reportDetails.status === 3 ||
        reportDetails.status === 4 ||
        reportDetails.status === 5) &&
      !reportDetails.hasFeedbackBeenSubmitted &&
      reportDetails.isWhistleblowerFeedbackEnabled
    ) {
      setShowFeedbackForm(true);
    }
  }, [reportDetails]);

  useEffect(() => {
    fetchReportDetailsDropdownHandler();
  }, [fetchReportDetailsDropdownHandler]);

  return (
    <>
      <SubmitFeedbackForm
        open={showFeedbackForm}
        handleClickSuccess={handleClickSuccess}
        handleClose={handleClickFeedback}
      />
      <SubmitFeedbackSuccess
        open={showFeedbackSuccess}
        handleClose={handleClickSuccess}
      />
      <div className='flex flex-col lg:flex-row gap-4'>
        <section className='flex-1'>
          <ReportDetails
            isLoading={isLoading}
            error={error}
            reportDetails={reportDetails}
            setReportDetails={setReportDetails}
            messageList={messageList}
            enableDownload={enableDownload}
          />
        </section>
        <section className='flex-1'>
          {reportDetails.conversation && (
            <Messages
              conversationId={reportDetails.conversation}
              setList={setList}
              setEnableDownload={handleEnableDownload}
            />
          )}
        </section>
      </div>
    </>
  );
};

export default ViewReportPage;
